import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../config/api';
import { PasswordInput } from '../components/PasswordInput';
import { setStoredUser } from '../utils/auth';

const inputClass =
  'w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent transition';

function Login() {
  const navigate = useNavigate();

  const [mode, setMode] = useState('login'); // login | forgot-email | forgot-reset
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const openForgotPassword = () => {
    setError('');
    setSuccess('');
    setForgotEmail(formData.email || '');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setMode('forgot-email');
  };

  const backToLogin = () => {
    setError('');
    setSuccess('');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setMode('login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = Array.isArray(data.detail) ? data.detail[0]?.msg : data.detail;
        setError(msg || 'Login failed');
        return;
      }
      setStoredUser(data.user);
      setSuccess('Login successful. Redirecting...');
      setTimeout(() => {
        navigate('/dashboard', { state: { user: data.user } });
      }, 800);
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/forgot-password/request'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = Array.isArray(data.detail) ? data.detail[0]?.msg : data.detail;
        setError(msg || 'Could not send reset code');
        return;
      }
      setSuccess(data.message || 'If an account exists for that email, a one-time code has been sent.');
      setMode('forgot-reset');
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/forgot-password/reset'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotEmail.trim(),
          otp: otp.trim(),
          password: newPassword,
          confirm_password: confirmPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = Array.isArray(data.detail) ? data.detail[0]?.msg : data.detail;
        setError(msg || 'Could not reset password');
        return;
      }
      setFormData((prev) => ({ ...prev, email: forgotEmail.trim(), password: '' }));
      setSuccess(data.message || 'Password updated successfully. You can sign in now.');
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setMode('login');
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const title =
    mode === 'login'
      ? 'Welcome Back'
      : mode === 'forgot-email'
        ? 'Forgot Password'
        : 'Reset Password';
  const subtitle =
    mode === 'login'
      ? 'Sign in to your account'
      : mode === 'forgot-email'
        ? 'Enter your account email to receive a one-time code'
        : 'Enter the code from your email and choose a new password';

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12 relative">
      <div className="absolute inset-0 z-0">
        <img
          src="/IMG_0945.jpg"
          alt="Background"
          className="w-full h-full object-cover opacity-55"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
      </div>

      <div className="relative z-10 max-w-md w-full">
        <div className="bg-gray-900 rounded-lg shadow-xl p-8 border border-gray-800">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">{title}</h1>
            <p className="text-gray-300">{subtitle}</p>
          </div>

          {success && (
            <div className="mb-4 text-green-400 text-sm text-center bg-green-400/10 py-2 px-3 rounded">
              {success}
            </div>
          )}
          {error && (
            <div className="mb-4 text-red-400 text-sm text-center bg-red-400/10 py-2 px-3 rounded">
              {error}
            </div>
          )}

          {mode === 'login' ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <PasswordInput
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoComplete="current-password"
                  className={inputClass}
                  placeholder="••••••••"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-white focus:ring-gray-600 border-gray-700 rounded bg-black"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-300">
                    Remember me
                  </label>
                </div>
                <div className="text-sm">
                  <button
                    type="button"
                    onClick={openForgotPassword}
                    className="font-medium text-white hover:text-gray-300"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full text-gray-900 font-semibold py-3 px-4 rounded-lg bg-[#eda448] hover:bg-[#d8932e] transition-colors focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-70"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          ) : null}

          {mode === 'forgot-email' ? (
            <form onSubmit={handleRequestOtp} className="space-y-6">
              <div>
                <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="forgot-email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  className={inputClass}
                  placeholder="you@example.com"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full text-gray-900 font-semibold py-3 px-4 rounded-lg bg-[#eda448] hover:bg-[#d8932e] transition-colors disabled:opacity-70"
              >
                {loading ? 'Sending…' : 'Send one-time code'}
              </button>
              <button
                type="button"
                onClick={backToLogin}
                className="w-full text-sm text-gray-300 hover:text-white"
              >
                Back to sign in
              </button>
            </form>
          ) : null}

          {mode === 'forgot-reset' ? (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="reset-email"
                  value={forgotEmail}
                  readOnly
                  className={`${inputClass} opacity-80 cursor-not-allowed`}
                />
              </div>
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-300 mb-2">
                  One-time code
                </label>
                <input
                  type="text"
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className={inputClass}
                  placeholder="6-digit code"
                />
              </div>
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-300 mb-2">
                  New Password
                </label>
                <PasswordInput
                  id="new-password"
                  name="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className={inputClass}
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-300 mb-2">
                  Confirm New Password
                </label>
                <PasswordInput
                  id="confirm-password"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className={inputClass}
                  placeholder="••••••••"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full text-gray-900 font-semibold py-3 px-4 rounded-lg bg-[#eda448] hover:bg-[#d8932e] transition-colors disabled:opacity-70"
              >
                {loading ? 'Updating…' : 'Update password'}
              </button>
              <div className="flex items-center justify-between gap-3 text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setError('');
                    setSuccess('');
                    setMode('forgot-email');
                  }}
                  className="text-gray-300 hover:text-white"
                >
                  Resend code
                </button>
                <button
                  type="button"
                  onClick={backToLogin}
                  className="text-gray-300 hover:text-white"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default Login;
